import { useState } from "react";
import { Upload, Col, Row, Typography, Progress, Select, Flex } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Card } from "@rumbledirect/ui";
import {
  DocumentChecklistItem,
  type UploadedFileInfo,
} from "../components/DocumentChecklistItem";

const { Dragger } = Upload;
const { Text } = Typography;

interface DocumentType {
  name: string;
  description: string;
  required: boolean;
  initialUploaded: boolean;
}

const requiredDocuments: DocumentType[] = [
  {
    name: "Schadeformulier",
    description:
      "Het Europees schadeformulier, ondertekend door beide partijen. Dit formulier is essentieel voor de afhandeling van de claim.",
    required: true,
    initialUploaded: false,
  },
  {
    name: "Foto's van de schade",
    description:
      "Minimaal 3 foto's van de schade vanuit verschillende hoeken. Zorg dat de schade duidelijk zichtbaar is.",
    required: true,
    initialUploaded: true,
  },
  {
    name: "Kopie rijbewijs",
    description: "Een leesbare kopie van het rijbewijs van de bestuurder.",
    required: true,
    initialUploaded: true,
  },
  {
    name: "Kopie kentekenbewijs",
    description:
      "Een kopie van het kentekenbewijs (deel 1 en deel 2) van het beschadigde voertuig.",
    required: true,
    initialUploaded: false,
  },
];

const optionalDocuments: DocumentType[] = [
  {
    name: "Proces verbaal",
    description:
      "Politierapport van het incident. Verplicht als de politie ter plaatse is geweest.",
    required: false,
    initialUploaded: false,
  },
  {
    name: "Getuigenverklaring",
    description:
      "Schriftelijke verklaring van eventuele getuigen van het incident.",
    required: false,
    initialUploaded: false,
  },
  {
    name: "Facturen / Offertes",
    description:
      "Bestaande reparatieoffertes of facturen die relevant zijn voor de schadebeoordeling.",
    required: false,
    initialUploaded: false,
  },
  {
    name: "Kopie groene kaart",
    description:
      "Het internationale verzekeringsbewijs (groene kaart) van de tegenpartij.",
    required: false,
    initialUploaded: false,
  },
  {
    name: "Dashcam beelden",
    description:
      "Video-opnames van een dashcam die het incident vastleggen. Ondersteunde formaten: MP4, AVI, MOV.",
    required: false,
    initialUploaded: false,
  },
];

const documentTypeOptions = [
  ...requiredDocuments.map((d) => ({ label: d.name, value: d.name })),
  ...optionalDocuments.map((d) => ({ label: d.name, value: d.name })),
  { label: "Overig", value: "overig" },
];

export const ClaimDossierDocumentsTab = () => {
  const [selectedDocType, setSelectedDocType] = useState<string>();
  const [uploads, setUploads] = useState<
    Record<string, UploadedFileInfo | null>
  >({});

  const handleUpload = (docName: string, file: UploadedFileInfo) => {
    setUploads((prev) => ({ ...prev, [docName]: file }));
  };

  const handleRemove = (docName: string) => {
    setUploads((prev) => ({ ...prev, [docName]: null }));
  };

  const isDocUploaded = (doc: DocumentType) =>
    doc.initialUploaded || uploads[doc.name] != null;

  const uploadedRequired = requiredDocuments.filter(isDocUploaded).length;
  const totalRequired = requiredDocuments.length;
  const progressPercent = Math.round((uploadedRequired / totalRequired) * 100);

  return (
    <Flex gap={20} vertical className="w-full">
      <Card title="Documentenoverzicht">
        <div className="mb-2">
          <Flex justify="space-between" align="center" className="mb-1">
            <Text strong>Voortgang verplichte documenten</Text>
            <Text type="secondary">
              {uploadedRequired} van {totalRequired} aangeleverd
            </Text>
          </Flex>
          <Progress
            percent={progressPercent}
            status={progressPercent === 100 ? "success" : "active"}
            strokeColor={
              progressPercent === 100
                ? "#52c41a"
                : { from: "#1677ff", to: "#1677ff" }
            }
          />
        </div>
      </Card>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={12}>
          <Card title="Verplichte Documenten" className="h-full">
            {requiredDocuments.map((doc) => (
              <DocumentChecklistItem
                key={doc.name}
                name={doc.name}
                description={doc.description}
                required={doc.required}
                initialUploaded={doc.initialUploaded}
                uploadedFile={uploads[doc.name] ?? null}
                onUpload={(file) => handleUpload(doc.name, file)}
                onRemove={() => handleRemove(doc.name)}
              />
            ))}
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card title="Optionele Documenten" className="h-full">
            {optionalDocuments.map((doc) => (
              <DocumentChecklistItem
                key={doc.name}
                name={doc.name}
                description={doc.description}
                required={doc.required}
                initialUploaded={doc.initialUploaded}
                uploadedFile={uploads[doc.name] ?? null}
                onUpload={(file) => handleUpload(doc.name, file)}
                onRemove={() => handleRemove(doc.name)}
              />
            ))}
          </Card>
        </Col>
      </Row>

      <Card title="Overige Documenten">
        <Text type="secondary" className="block mb-4">
          Gebruik dit veld om documenten te uploaden die niet in de bovenstaande
          lijst staan, of om aanvullende bestanden toe te voegen.
        </Text>
        <div className="mb-4">
          <Text className="block mb-1">Documenttype</Text>
          <Select
            placeholder="Selecteer het type document"
            options={documentTypeOptions}
            value={selectedDocType}
            onChange={setSelectedDocType}
            className="w-full"
            style={{ maxWidth: 400 }}
            allowClear
          />
        </div>
        <Dragger
          name="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.mp4,.avi,.mov"
          customRequest={() => {}}
          showUploadList
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Sleep bestanden hierheen of klik om te uploaden
          </p>
          <p className="ant-upload-hint">
            Ondersteunde formaten: PDF, JPG, PNG, DOC, DOCX, MP4, AVI, MOV (max.
            10MB)
          </p>
        </Dragger>
      </Card>
    </Flex>
  );
};
